import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useReactFlow, useStoreApi, useViewport } from 'reactflow';
import type { Node } from 'reactflow';
import type {
  BoardDrawKind,
  BoardDrawPoint,
  BoardDrawTool,
  BoardDrawing,
  BoardImageData,
} from '../../types';
import { CALLOUT_STROKE, FRAME_FILL, HIGHLIGHTER_OPACITY } from './boardConstants';
import { ENG_CALLOUT_FILL, orthogonalElbow } from '../pdf/engineeringMarkup';
import {
  constrainEnd,
  drawingBounds,
  fromImageNorm,
  genDrawId,
  hitTestImage,
  calloutPartAtPoint,
  drawingIsFront,
  imageCalloutsInFlow,
  imageMediaRect,
  maxNodeZIndex,
  moveDrawing,
  moveDrawingPoint,
  resizeDrawing,
  strokeHitsPoint,
  toImageNorm,
  topmostAtPoint,
  type ResizeHandle,
} from './drawUtils';
import { DrawingsSvg } from './drawingsSvg';

export function BoardDrawBehind({
  drawings,
  selectedId,
}: {
  drawings: BoardDrawing[];
  selectedId?: string | null;
}) {
  const { x, y, zoom } = useViewport();
  if (drawings.length === 0) return null;
  return (
    <div className="board-draw-behind" aria-hidden>
      <DrawingsSvg
        className="board-draw-svg"
        style={{ width: '100%', height: '100%' }}
        innerTransform={`translate(${x} ${y}) scale(${zoom})`}
        drawings={drawings}
        selectedId={selectedId}
      />
    </div>
  );
}

export type DrawingSelection = { id: string; target: 'canvas' | string };

export interface BoardDrawLayerHandle {
  beginEdit: (
    drawing: BoardDrawing,
    target: 'canvas' | string,
    e: React.PointerEvent,
    handle?: ResizeHandle,
    pointIndex?: number,
  ) => void;
}

export interface CalloutEdit {
  id: string;
  target: 'canvas' | string;
  text: string;
  screen: { x: number; y: number };
}

interface BoardDrawLayerProps {
  tool: BoardDrawTool;
  color: string;
  strokeWidth: number;
  spaceHeld: boolean;
  canvasDrawings: BoardDrawing[];
  nodes: Node[];
  selected: DrawingSelection | null;
  onSelect: (sel: DrawingSelection | null) => void;
  onAddCanvas: (drawing: BoardDrawing) => void;
  onAddImage: (nodeId: string, drawing: BoardDrawing) => void;
  onEraseCanvas: (ids: string[]) => void;
  onEraseImage: (nodeId: string, ids: string[]) => void;
  onUpdateDrawing: (target: 'canvas' | string, drawing: BoardDrawing) => void;
  onUpdateCallout: (target: 'canvas' | string, id: string, text: string) => void;
  onActiveChange?: (active: boolean) => void;
  authorInitials?: string;
  authorName?: string;
  authorId?: string;
}

interface StrokeSession {
  target: 'canvas' | string;
  media?: { x: number; y: number; width: number; height: number };
  drawing: BoardDrawing;
}

interface EditSession {
  mode: 'move' | 'resize' | 'point';
  handle?: ResizeHandle;
  pointIndex?: number;
  target: 'canvas' | string;
  start: BoardDrawPoint;
  orig: BoardDrawing;
  media?: { x: number; y: number; width: number; height: number };
}

function findDrawing(
  sel: DrawingSelection,
  canvasDrawings: BoardDrawing[],
  nodes: Node[],
): BoardDrawing | null {
  if (sel.target === 'canvas') return canvasDrawings.find((d) => d.id === sel.id) || null;
  const node = nodes.find((n) => n.id === sel.target);
  const drawings = ((node?.data as BoardImageData | undefined)?.drawings) || [];
  return drawings.find((d) => d.id === sel.id) || null;
}

const BoardDrawLayer = forwardRef<BoardDrawLayerHandle, BoardDrawLayerProps>((props, ref) => {
  const {
    tool,
    color,
    strokeWidth,
    spaceHeld,
    canvasDrawings,
    nodes,
    selected,
    onSelect,
    onAddCanvas,
    onAddImage,
    onEraseCanvas,
    onEraseImage,
    onUpdateDrawing,
    onUpdateCallout,
    onActiveChange,
    authorInitials,
    authorName,
    authorId,
  } = props;
  const rf = useReactFlow();
  const storeApi = useStoreApi();
  const { x, y, zoom } = useViewport();
  const layerRef = useRef<HTMLDivElement>(null);

  // Shapes sit in an overlay outside React Flow, so trackpad wheel never
  // reaches the pane. Use the same wheelDelta / pan math as ZoomPane.
  useEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      const { d3Zoom, d3Selection } = storeApi.getState();
      if (!d3Zoom || !d3Selection) return;
      event.preventDefault();
      event.stopPropagation();
      const currentZoom = d3Selection.property('__zoom')?.k || 1;
      if (event.ctrlKey) {
        const pane = d3Selection.node() as Element | null;
        const rect = pane?.getBoundingClientRect();
        const point: [number, number] = rect
          ? [event.clientX - rect.left, event.clientY - rect.top]
          : [event.clientX, event.clientY];
        const macPinch = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
        const pinchDelta =
          -event.deltaY *
          (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002) *
          (macPinch ? 10 : 1);
        d3Zoom.scaleTo(d3Selection, currentZoom * Math.pow(2, pinchDelta), point);
        return;
      }
      const deltaNormalize = event.deltaMode === 1 ? 20 : 1;
      d3Zoom.translateBy(
        d3Selection,
        -((event.deltaX * deltaNormalize) / currentZoom) * 0.5,
        -((event.deltaY * deltaNormalize) / currentZoom) * 0.5,
      );
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [storeApi]);
  const [preview, setPreview] = useState<BoardDrawing | null>(null);
  const [calloutEdit, setCalloutEdit] = useState<CalloutEdit | null>(null);
  const sessionRef = useRef<StrokeSession | null>(null);
  const editRef = useRef<EditSession | null>(null);
  const erasedRef = useRef<Set<string>>(new Set());

  const capturing = tool !== 'select' && !spaceHeld && !calloutEdit;

  const flowPoint = useCallback(
    (e: { clientX: number; clientY: number }): BoardDrawPoint =>
      rf.screenToFlowPosition({ x: e.clientX, y: e.clientY }),
    [rf],
  );

  const makeDrawing = useCallback(
    (kind: BoardDrawKind, points: BoardDrawPoint[], imageLocal: boolean, mediaWidth?: number): BoardDrawing => {
      const isHi = kind === 'highlighter';
      let width = kind === 'callout' ? CALLOUT_STROKE : strokeWidth;
      if (imageLocal && mediaWidth && mediaWidth > 0) {
        width = width / mediaWidth;
      }
      return {
        id: genDrawId(),
        kind,
        color,
        width,
        points,
        opacity: isHi ? HIGHLIGHTER_OPACITY : undefined,
        ...(kind === 'rect' ? { fill: FRAME_FILL } : {}),
        ...(kind === 'callout'
          ? {
              fill: ENG_CALLOUT_FILL,
              authorInitials: authorInitials || undefined,
              authorName: authorName || undefined,
              authorId: authorId || undefined,
            }
          : {}),
      };
    },
    [color, strokeWidth, authorInitials, authorName, authorId],
  );

  const eraseAt = useCallback(
    (fp: BoardDrawPoint) => {
      const threshold = 10 / (zoom || 1);
      const hitIds: string[] = [];
      for (const d of canvasDrawings) {
        if (erasedRef.current.has(`c:${d.id}`)) continue;
        if (strokeHitsPoint(d, fp, threshold)) {
          hitIds.push(d.id);
          erasedRef.current.add(`c:${d.id}`);
        }
      }
      if (hitIds.length) onEraseCanvas(hitIds);

      const imgHit = hitTestImage(fp, nodes);
      if (!imgHit) return;
      const node = nodes.find((n) => n.id === imgHit.nodeId);
      const drawings = ((node?.data as BoardImageData | undefined)?.drawings) || [];
      const norm = toImageNorm(fp, imgHit.media);
      const imgThreshold = threshold / imgHit.media.width;
      const imgIds: string[] = [];
      for (const d of drawings) {
        if (erasedRef.current.has(`i:${imgHit.nodeId}:${d.id}`)) continue;
        if (strokeHitsPoint(d, norm, imgThreshold)) {
          imgIds.push(d.id);
          erasedRef.current.add(`i:${imgHit.nodeId}:${d.id}`);
        }
      }
      if (imgIds.length) onEraseImage(imgHit.nodeId, imgIds);
    },
    [canvasDrawings, nodes, onEraseCanvas, onEraseImage, zoom],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!capturing || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      onActiveChange?.(true);

      const fp = flowPoint(e);
      if (tool === 'eraser') {
        erasedRef.current = new Set();
        eraseAt(fp);
        return;
      }

      const kind = tool as BoardDrawKind;
      // Frames are canvas backdrops — never pin them to a photo.
      const img = kind === 'rect' ? null : hitTestImage(fp, nodes);
      const start = img ? toImageNorm(fp, img.media) : fp;
      const next = makeDrawing(kind, [start, start], !!img, img?.media.width);
      sessionRef.current = {
        target: img ? img.nodeId : 'canvas',
        media: img?.media,
        drawing: next,
      };
      setPreview(
        img
          ? { ...next, points: [fp, fp], width: strokeWidth }
          : next,
      );
    },
    [capturing, flowPoint, tool, nodes, makeDrawing, eraseAt, strokeWidth, onActiveChange],
  );

  const applyEdit = useCallback(
    (e: { clientX: number; clientY: number; shiftKey?: boolean }) => {
      const edit = editRef.current;
      if (!edit) return;
      const fp = flowPoint(e);
      const local = edit.media ? toImageNorm(fp, edit.media) : fp;
      const start = edit.start;
      let next: BoardDrawing;
      if (edit.mode === 'point' && edit.pointIndex != null) {
        next = moveDrawingPoint(edit.orig, edit.pointIndex, local);
      } else if (edit.mode === 'move') {
        next = moveDrawing(edit.orig, local.x - start.x, local.y - start.y);
      } else if (edit.handle) {
        next = resizeDrawing(edit.orig, edit.handle, local, !!e.shiftKey);
      } else {
        return;
      }
      onUpdateDrawing(edit.target, next);
    },
    [flowPoint, onUpdateDrawing],
  );

  const finishEdit = useCallback(() => {
    if (!editRef.current) return;
    editRef.current = null;
    onActiveChange?.(false);
  }, [onActiveChange]);

  const startEdit = useCallback(
    (
      nextDrawing: BoardDrawing,
      target: 'canvas' | string,
      e: React.PointerEvent,
      handle?: ResizeHandle,
      pointIndex?: number,
    ) => {
      const fp = flowPoint(e);
      const media =
        target === 'canvas'
          ? undefined
          : imageMediaRect(nodes.find((n) => n.id === target) as Node, nodes) || undefined;
      const start = media ? toImageNorm(fp, media) : fp;
      editRef.current = {
        mode: handle ? 'resize' : pointIndex != null ? 'point' : 'move',
        handle,
        pointIndex,
        target,
        start,
        orig: nextDrawing,
        media,
      };
      onSelect({ id: nextDrawing.id, target });
      onActiveChange?.(true);
      const onMove = (ev: PointerEvent) => applyEdit(ev);
      const onUp = () => {
        finishEdit();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [flowPoint, nodes, onSelect, onActiveChange, applyEdit, finishEdit],
  );

  useImperativeHandle(ref, () => ({ beginEdit: startEdit }), [startEdit]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (editRef.current) {
        applyEdit(e);
        return;
      }
      if (!capturing) return;
      const fp = flowPoint(e);
      if (tool === 'eraser') {
        if (e.buttons !== 1) return;
        eraseAt(fp);
        return;
      }
      const session = sessionRef.current;
      if (!session) return;
      const rawEnd = session.media ? toImageNorm(fp, session.media) : fp;
      const start = session.drawing.points[0];
      const end = constrainEnd(session.drawing.kind, start, rawEnd, e.shiftKey);
      let nextPoints: BoardDrawPoint[];
      if (session.drawing.kind === 'pen' || session.drawing.kind === 'highlighter') {
        const last = session.drawing.points[session.drawing.points.length - 1];
        const minDist = session.media ? 0.004 : 1.5;
        const far = Math.hypot(end.x - last.x, end.y - last.y) >= minDist;
        nextPoints = far ? [...session.drawing.points, end] : session.drawing.points;
      } else if (session.drawing.kind === 'callout' && e.shiftKey) {
        nextPoints = [start, orthogonalElbow(start, rawEnd), rawEnd];
      } else {
        nextPoints = [start, end];
      }
      session.drawing = { ...session.drawing, points: nextPoints };

      if (session.media) {
        const flowPts = nextPoints.map((p) => ({
          x: session.media!.x + p.x * session.media!.width,
          y: session.media!.y + p.y * session.media!.height,
        }));
        setPreview({ ...session.drawing, points: flowPts, width: strokeWidth });
      } else {
        setPreview(session.drawing);
      }
    },
    [capturing, flowPoint, tool, eraseAt, strokeWidth, applyEdit],
  );

  const finishStroke = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    setPreview(null);
    onActiveChange?.(false);
    if (!session) return;
    const pts = session.drawing.points;
    if (pts.length < 2) return;
    const a = pts[0];
    const b = pts[pts.length - 1];
    const moved = Math.hypot(b.x - a.x, b.y - a.y);
    const minMove = session.media ? 0.008 : 2;
    if (moved < minMove && session.drawing.kind !== 'pen' && session.drawing.kind !== 'highlighter') {
      return;
    }
    if (session.target === 'canvas') onAddCanvas(session.drawing);
    else onAddImage(session.target, session.drawing);
    onSelect({ id: session.drawing.id, target: session.target });

    if (session.drawing.kind === 'callout') {
      const label = session.drawing.points[session.drawing.points.length - 1];
      const screen = session.media
        ? rf.flowToScreenPosition({
            x: session.media.x + label.x * session.media.width,
            y: session.media.y + label.y * session.media.height,
          })
        : rf.flowToScreenPosition(label);
      setCalloutEdit({
        id: session.drawing.id,
        target: session.target,
        text: '',
        screen,
      });
    }
  }, [onAddCanvas, onAddImage, rf, onActiveChange, onSelect]);

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (tool === 'eraser') {
        erasedRef.current = new Set();
        onActiveChange?.(false);
        return;
      }
      if (e.button !== 0) return;
      finishStroke();
    },
    [tool, finishStroke, onActiveChange],
  );

  const imageCallouts = useMemo(() => imageCalloutsInFlow(nodes), [nodes]);
  const maxZ = useMemo(() => maxNodeZIndex(nodes), [nodes]);
  const overlayDrawings = useMemo(
    () => [
      ...canvasDrawings.filter((d) => drawingIsFront(d, maxZ)),
      ...imageCallouts.filter((c) => drawingIsFront(c.flow, maxZ)).map((c) => c.flow),
    ],
    [canvasDrawings, imageCallouts, maxZ],
  );
  const hitDrawings = useMemo(
    () => [...canvasDrawings, ...imageCallouts.map((c) => c.flow)],
    [canvasDrawings, imageCallouts],
  );

  const handleCalloutDoubleClick = useCallback(
    (drawing: BoardDrawing) => {
      const owned = imageCallouts.find((c) => c.flow.id === drawing.id);
      const label = drawing.points[drawing.points.length - 1] || drawing.points[0];
      setCalloutEdit({
        id: drawing.id,
        target: owned?.target ?? 'canvas',
        text: (owned?.orig ?? drawing).text || '',
        screen: rf.flowToScreenPosition(label),
      });
    },
    [rf, imageCallouts],
  );

  useEffect(() => {
    if (tool !== 'select' || spaceHeld) return;
    const root = layerRef.current?.parentElement;
    if (!root) return;
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement | null;
      if (
        t?.closest(
          'input, textarea, [contenteditable="true"], .board-draw-toolbar, .board-toolbar, .board-node-toolbar, .board-draw-handle, .board-draw-callout-input, .board-draw-capture',
        )
      ) {
        return;
      }
      const fp = flowPoint(e);
      const top = topmostAtPoint(fp, nodes, hitDrawings, 8 / (zoom || 1), (d) => {
        const owned = imageCallouts.find((c) => c.flow.id === d.id);
        return owned?.target ?? 'canvas';
      });
      if (top?.kind !== 'drawing') return;
      e.preventDefault();
      e.stopPropagation();
      const owned = imageCallouts.find((c) => c.flow.id === top.drawing.id);
      const orig = owned?.orig ?? top.drawing;
      const target = owned?.target ?? 'canvas';
      let pointIndex: number | undefined;
      if (top.drawing.kind === 'callout') {
        const part = calloutPartAtPoint(top.drawing, fp, 12 / (zoom || 1));
        const last = top.drawing.points.length - 1;
        if (typeof part === 'number') pointIndex = part;
        else if (part === 'box') pointIndex = last;
        else if (part === 'pin') pointIndex = 0;
      }
      startEdit(orig, target, e as unknown as React.PointerEvent, undefined, pointIndex);
    };
    const onDbl = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, [contenteditable="true"]')) return;
      const fp = flowPoint(e);
      const top = topmostAtPoint(fp, nodes, hitDrawings, 8 / (zoom || 1));
      if (top?.kind !== 'drawing' || top.drawing.kind !== 'callout') return;
      e.preventDefault();
      e.stopPropagation();
      handleCalloutDoubleClick(top.drawing);
    };
    root.addEventListener('pointerdown', onDown, true);
    root.addEventListener('dblclick', onDbl, true);
    return () => {
      root.removeEventListener('pointerdown', onDown, true);
      root.removeEventListener('dblclick', onDbl, true);
    };
  }, [tool, spaceHeld, flowPoint, nodes, hitDrawings, imageCallouts, zoom, startEdit, handleCalloutDoubleClick]);

  const commitCallout = useCallback(
    (text: string) => {
      if (!calloutEdit) return;
      onUpdateCallout(calloutEdit.target, calloutEdit.id, text);
      setCalloutEdit(null);
    },
    [calloutEdit, onUpdateCallout],
  );

  const selectedDrawing = selected ? findDrawing(selected, canvasDrawings, nodes) : null;
  const selectedFlowDrawing = (() => {
    if (!selected || !selectedDrawing) return null;
    if (selected.target === 'canvas') return selectedDrawing;
    const media = imageMediaRect(nodes.find((n) => n.id === selected.target) as Node, nodes);
    if (!media) return selectedDrawing;
    return {
      ...selectedDrawing,
      points: selectedDrawing.points.map((p) => fromImageNorm(p, media)),
    };
  })();

  const showRectHandles =
    tool === 'select' &&
    selectedFlowDrawing &&
    (selectedFlowDrawing.kind === 'rect' || selectedFlowDrawing.kind === 'ellipse');
  const showCalloutHandles = tool === 'select' && selectedFlowDrawing?.kind === 'callout';

  const handleSize = 9 / (zoom || 1);
  const b = selectedFlowDrawing ? drawingBounds(selectedFlowDrawing) : null;
  const handles: { id: ResizeHandle; x: number; y: number }[] = b
    ? [
        { id: 'nw', x: b.x, y: b.y },
        { id: 'ne', x: b.x + b.width, y: b.y },
        { id: 'sw', x: b.x, y: b.y + b.height },
        { id: 'se', x: b.x + b.width, y: b.y + b.height },
      ]
    : [];

  return (
    <div ref={layerRef} className="board-draw-layer" aria-hidden={tool === 'select' && !selected}>
      <DrawingsSvg
        className="board-draw-svg"
        style={{ width: '100%', height: '100%' }}
        innerTransform={`translate(${x} ${y}) scale(${zoom})`}
        drawings={overlayDrawings}
        preview={preview}
        selectedId={selected?.id ?? null}
      >
        {showCalloutHandles && selected && selectedDrawing && selectedFlowDrawing && (
          <>
            {selectedFlowDrawing.points.map((p, i) => (
              <circle
                key={`callout-h-${i}`}
                className="board-draw-handle"
                cx={p.x}
                cy={p.y}
                r={handleSize * 0.7}
                fill="#fff"
                stroke="#6366f1"
                strokeWidth={1.25 / (zoom || 1)}
                style={{ pointerEvents: 'all', cursor: i === 0 ? 'crosshair' : 'move' }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  startEdit(selectedDrawing, selected.target, e, undefined, i);
                }}
              />
            ))}
          </>
        )}
        {showRectHandles && b && selected && selectedDrawing && (
          <>
            <rect
              x={b.x}
              y={b.y}
              width={b.width}
              height={b.height}
              fill="none"
              stroke="#6366f1"
              strokeWidth={1.5 / (zoom || 1)}
              strokeDasharray={`${4 / (zoom || 1)} ${3 / (zoom || 1)}`}
              style={{ pointerEvents: 'none' }}
            />
            {handles.map((h) => (
              <rect
                key={h.id}
                className={`board-draw-handle board-draw-handle--${h.id}`}
                x={h.x - handleSize / 2}
                y={h.y - handleSize / 2}
                width={handleSize}
                height={handleSize}
                fill="#fff"
                stroke="#6366f1"
                strokeWidth={1.25 / (zoom || 1)}
                style={{ pointerEvents: 'all', cursor: `${h.id}-resize` }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  startEdit(selectedDrawing, selected.target, e, h.id);
                }}
              />
            ))}
          </>
        )}
      </DrawingsSvg>

      {capturing && (
        <div
          className={`board-draw-capture board-draw-capture--${tool}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      )}

      {calloutEdit && (
        <input
          autoFocus
          className="board-draw-callout-input"
          style={{ left: calloutEdit.screen.x, top: calloutEdit.screen.y }}
          defaultValue={calloutEdit.text}
          placeholder="Add a label…"
          onBlur={(e) => commitCallout(e.target.value.trim())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitCallout((e.target as HTMLInputElement).value.trim());
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setCalloutEdit(null);
            }
          }}
        />
      )}
    </div>
  );
});

BoardDrawLayer.displayName = 'BoardDrawLayer';

export default BoardDrawLayer;
